import React from 'react';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { t } from 'ttag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import FormGroup from '@material-ui/core/FormGroup';
import Dialog from '@material-ui/core/Dialog';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ukLocale from 'date-fns/locale/ru';
import Switch from '@material-ui/core/Switch';
import moment from 'moment';
import * as Yup from 'yup';
import ErrorMessage from '../../../components/ErrorMessage';
import DataTable from '../../../components/DataTable';
import useApi from '../../../api';
import useEditForm from '../../../hooks/useEditForm';
import useCreateForm from '../../../hooks/useCreateForm';
import FormBox from '../../../components/FormBox';
import useUser from '../../../auth/hooks';
import {
  calculateAge,
  formatPublicDate,
  isIntersection,
} from '../../../utils/helpers';
import { BASE_STATUS, USER_STATUS, USER_TYPES } from '../../../utils/enums';
import Loading from '../../../components/Loading';
import FormField from '../../../components/FormField';
import NewForm from '../../../components/NewForm';
import SelectChooser from '../../../components/SelectChooser';
import AthleteViewDialog from '../../../components/AthleteViewDialog';
import { isTrainer } from '../../../utils/permissions';
import LoadingButton from '../../../components/LoadingButton';
import { extractDataError } from '../../../utils/errors';
import ReasonDialogForm from '../../../components/ReasonDialogForm';
import useNotifier from '../../../components/Messages/hooks';
import AthleteGroupChangeDialog from '../../../components/AthleteGroupChangeDialog';
import Link from "@material-ui/core/Link";

const DAY_NUM = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

const DAY_TYPE = {
  weekdays: 'weekdays',
  weekends: 'weekends',
};

const DAYS_OPTIONS = [
  { name: t`Понеділок`, value: DAY_NUM.monday },
  { name: t`Вівторок`, value: DAY_NUM.tuesday },
  { name: t`Середа`, value: DAY_NUM.wednesday },
  { name: t`Четвер`, value: DAY_NUM.thursday },
  { name: t`Пятниця`, value: DAY_NUM.friday },
  { name: t`Субота`, value: DAY_NUM.saturday },
  { name: t`Неділя`, value: DAY_NUM.sunday },
];

const DAY_TYPE_OPTIONS = [
  { name: t`Будні дні`, value: DAY_TYPE.weekdays },
  { name: t`Вихідні дні`, value: DAY_TYPE.weekends },
];

const initScheduleDayData = (data, defaultGymId) => ({
  dayNum: data.dayNum,
  selected: data.selected || false,
  startAt: data.startAt || moment(),
  endAt: data.endAt || moment(),
  gymId: data.gymId || defaultGymId,
});

const initSchedule = (schedule) => {
  const data = {};
  schedule && schedule.map((r) => (data[r.dayNum] = r));
  return {
    [DAY_NUM.monday]: initScheduleDayData(data[DAY_NUM.monday] || {}),
    [DAY_NUM.tuesday]: initScheduleDayData(data[DAY_NUM.tuesday] || {}),
    [DAY_NUM.wednesday]: initScheduleDayData(data[DAY_NUM.wednesday] || {}),
    [DAY_NUM.thursday]: initScheduleDayData(data[DAY_NUM.thursday] || {}),
    [DAY_NUM.friday]: initScheduleDayData(data[DAY_NUM.friday] || {}),
    [DAY_NUM.saturday]: initScheduleDayData(data[DAY_NUM.saturday] || {}),
    [DAY_NUM.sunday]: initScheduleDayData(data[DAY_NUM.sunday] || {}),
  };
};

const prepareOnComplete = (data) =>
  DAYS_OPTIONS.map((d) => ({
    dayNum: d.value || '',
    selected: data[d.value].selected || false,
    startAt: data[d.value].startAt || '',
    endAt: data[d.value].endAt || '',
    gymId: data[d.value].gymId || '',
  }));

const ScheduleForm = ({ data, defaultGymId, onChange }) => {
  const api = useApi();
  const [preset, setPreset] = React.useState({
    weekdays: initScheduleDayData({}, defaultGymId),
    weekends: initScheduleDayData({}, defaultGymId),
  });
  const [state, setState] = React.useState(initSchedule(data));
  const handleOnChange = (day, name, value) => {
    const newState = { ...state };
    newState[day][name] = value;
    setState(newState);
    onChange(prepareOnComplete(newState));
  };

  const onChangePreset = (dayType, name, value) => {
    const newState = { ...preset };
    newState[dayType][name] = value;
    setPreset(newState);
  };

  const onApplyPreset = () => {
    const newState = { ...state };
    const isWeekends = (v) => [DAY_NUM.saturday, DAY_NUM.sunday].includes(v);
    DAYS_OPTIONS.filter((d) => !isWeekends(d.value)).map((d) => {
      newState[d.value].selected = preset.weekdays.selected;
      newState[d.value].startAt = preset.weekdays.startAt;
      newState[d.value].endAt = preset.weekdays.endAt;
      newState[d.value].gymId = preset.weekdays.gymId;
    });

    DAYS_OPTIONS.filter((d) => isWeekends(d.value)).map((d) => {
      newState[d.value].selected = preset.weekends.selected;
      newState[d.value].startAt = preset.weekends.startAt;
      newState[d.value].endAt = preset.weekends.endAt;
      newState[d.value].gymId = preset.weekends.gymId;
    });

    setState(newState);
    onChange(prepareOnComplete(newState));
  };

  return (
    <FormGroup>
      <Typography
        align='center'
        variant='caption'
      >{t`Застосувати для всіх днів`}</Typography>
      <br />
      <Paper
        style={{
          margin: '10px 0 10px 0',
          padding: '20px',
        }}
        elevation={3}
      >
        {DAY_TYPE_OPTIONS.map((d) => (
          <Grid key={d.value} container spacing={2}>
            <Grid item lg={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preset[d.value].selected}
                    onChange={(e, v) => onChangePreset(d.value, 'selected', v)}
                    color='primary'
                  />
                }
                label={d.name}
              />
            </Grid>
            <Grid item lg={3}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ukLocale}>
                <TimePicker
                  ampm={false}
                  label={t`Час початку`}
                  value={preset[d.value].startAt}
                  onChange={(v) => onChangePreset(d.value, 'startAt', v)}
                  cancelLabel={t`Відміна`}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item lg={3}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ukLocale}>
                <TimePicker
                  ampm={false}
                  label={t`Час закінчення`}
                  value={preset[d.value].endAt}
                  onChange={(v) => onChangePreset(d.value, 'endAt', v)}
                  cancelLabel={t`Відміна`}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item lg={3}>
              <FormField
                required
                fullWidth
                query={api.schema.gyms.many()[0]}
                preInit
                dataKey='gyms'
                variables={{}}
                type='remoteSelect'
                margin='normal'
                label={t`Виберіть зал`}
                name='gymId'
                variant='outlined'
                value={preset[d.value].gymId}
                labelKey='name'
                onChange={(v) =>
                  onChangePreset(d.value, 'gymId', v.target.value)
                }
              />
            </Grid>
          </Grid>
        ))}
        <Grid container justify='center' alignItems='center'>
          <Grid item>
            <Button
              color='primary'
              variant='outlined'
              onClick={onApplyPreset}
              startIcon={<PlaylistAddCheckIcon />}
            >
              {t`Застосувати`}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <br />
      <Divider />
      <br />
      {DAYS_OPTIONS.map((d) => (
        <Grid key={d.value} container spacing={2}>
          <Grid item lg={3}>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  checked={state[d.value].selected}
                  onChange={(e, v) => handleOnChange(d.value, 'selected', v)}
                />
              }
              label={d.name}
            />
          </Grid>
          <Grid item lg={3}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ukLocale}>
              <TimePicker
                ampm={false}
                label={t`Час початку`}
                value={state[d.value].startAt}
                onChange={(v) => handleOnChange(d.value, 'startAt', v)}
                cancelLabel={t`Відміна`}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item lg={3}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ukLocale}>
              <TimePicker
                ampm={false}
                label={t`Час закінчення`}
                value={state[d.value].endAt}
                onChange={(v) => handleOnChange(d.value, 'endAt', v)}
                cancelLabel={t`Відміна`}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item lg={3}>
            <FormField
              required
              fullWidth
              query={api.schema.gyms.many()[0]}
              preInit
              dataKey='gyms'
              variables={{}}
              type='remoteSelect'
              margin='normal'
              label={t`Виберіть зал`}
              name='gymId'
              variant='outlined'
              value={state[d.value].gymId}
              labelKey='name'
              onChange={(v) => handleOnChange(d.value, 'gymId', v.target.value)}
            />
          </Grid>
        </Grid>
      ))}
    </FormGroup>
  );
};

const AthletesForm = ({
  data,
  onChange,
  submit,
  trainerId,
  groupId,
  canSubmit,
  section,
}) => {
  const { user: authUser } = useUser();
  const api = useApi();
  const pushMessage = useNotifier();
  const [showReasonDialog, setStateShowReasonDialog] = React.useState({
    objId: null,
  });
  const [state, setState] = React.useState({
    data,
    selected: null,
    viewAthlete: null,
    changeAthleteGroup: null,
  });

  const handleOnEdit = (r) => {
    if (groupId) {
      window.open(
        `${api.router.athletes.edit(r.id)}?next=${api.router.groups.edit(
          groupId
        )}`,
        '_blank'
      );
    } else {
      window.open(
        `${api.router.athletes.edit(r.id)}?next=${api.router.groups.create()}`,
        '_blank'
      );
    }
  };

  const onSelect = (v) => {
    setState({ ...state, selected: v.target.value });
    const newState = { ...state, data: v.target.value };
    setState(newState);
    onChange(newState.data);
  };

  const onDelete = (v) => {
    const newState = {
      ...state,
      data: state.data.filter((r) => r.id !== v.id),
    };
    setState(newState);
    onChange(newState.data);
  };

  const handleOnView = (d) => {
    setState({ ...state, viewAthlete: d });
  };

  const handleOnChangeGroup = (e, data) => {
    setState({ ...state, changeAthleteGroup: data });
  };

  const handleOnArchive = (rowData) => {
    setStateShowReasonDialog({ ...showReasonDialog, objId: rowData.id });
  };

  const [archivateAthlete] = useMutation(api.schema.athletes.archivate()[0], {
    onCompleted: (d) => {
      const { archivateAthlete: res } = d;
      if (res.ok) {
        pushMessage({ text: 'Успішно додано до архіву' });
      } else {
        const dataError = extractDataError(res.errors);
        pushMessage({ text: dataError });
      }
    },
  });

  const [updateGroup] = useMutation(api.schema.athletes.changeGroup()[0], {
    onCompleted: (d) => {
      const { updateAthleteGroup: res } = d;
      if (res.ok) {
        pushMessage({ text: 'Успішно переведено в іншу групу' });
      } else {
        const dataError = extractDataError(res.errors);
        pushMessage({ text: dataError });
      }
    },
  });
  return (
    <>
      <Paper
        elevation={3}
        style={{
          margin: '10px 0 10px 0',
          padding: '10px',
        }}
      >
        <SelectChooser
          value={data || []}
          onChange={onSelect}
          submitForm={submit}
          canSubmit={canSubmit}
          onView={handleOnView}
          onEdit={(rowData, r) => handleOnEdit(r)}
          onArvitate={(event, rowData) => handleOnArchive(rowData)}
          query={api.schema.athletes.athletesForGroup()[0]}
          variables={
            isTrainer(authUser)
              ? { inputFilter: { trainerId, status: BASE_STATUS.active } }
              : { inputFilter: { trainerId, status: BASE_STATUS.active } }
          }
          dataKey='athletesForGroup'
          valueKey='id'
          labelKey='fullName'
          groupId={groupId}
          trainerId={trainerId}
          section={section}
        />
        <AthleteViewDialog
          open={!!state.viewAthlete}
          objId={state.viewAthlete && state.viewAthlete.id}
          onClose={() => setState({ ...state, viewAthlete: null })}
        />
        {state.changeAthleteGroup && (
          <Dialog
            open={!!state.changeAthleteGroup}
            onClose={() => setState({ ...state, changeAthleteGroup: null })}
          >
            <AthleteGroupChangeDialog
              currentGroupId={groupId}
              handleChangeGroup={(newGroupId) => {
                updateGroup({
                  variables: {
                    athleteId: state.changeAthleteGroup.id,
                    fromGroupId: groupId,
                    toGroupId: newGroupId,
                  },
                });
                const newState = {
                  ...state,
                  data: state.data.filter(
                    (r) => r.id !== state.changeAthleteGroup.id
                  ),
                  changeAthleteGroup: null,
                };
                setState(newState);
                onChange(newState.data);
              }}
            />
          </Dialog>
        )}
      </Paper>
      {data && data.length > 0 && (
        <DataTable
          toolbar={false}
          pageSize={Math.min(10, 20)}
          columns={[
            { title: t`Id`, field: 'id', editable: 'never' },
            { title: t`Ім'я`, field: 'fullName' },
            {
              title: t`Дата народження`,
              field: 'birthday',
              render: (r) =>
                `${formatPublicDate(r.birthday)} (${calculateAge(r.birthday)})`,
            },
          ]}
          data={data}
          onEdit={(e, r) => handleOnEdit(r)}
          onView={handleOnView}
          onDelete={onDelete}
          onArvitate={(event, rowData) => handleOnArchive(rowData)}
          onChangeGroup={
            authUser.isAdmin || authUser.isSuperManager
              ? handleOnChangeGroup
              : undefined
          }
        />
      )}
      {showReasonDialog.objId && (
        <ReasonDialogForm
          open={!!showReasonDialog.objId}
          onClose={() => setStateShowReasonDialog({ ...state, objId: null })}
          onAccept={async (data) => {
            const athleteId = showReasonDialog.objId;
            setStateShowReasonDialog({ ...state, objId: null });
            const { data: res } = await archivateAthlete({
              variables: { id: athleteId, reason: data.reason },
            });
            // remove from group only if athlete was really archived
            if (!res || !res.archivateAthlete.ok) {
              return;
            }
            const newState = {
              ...state,
              data: state.data.filter((r) => r.id !== athleteId),
            };
            setState(newState);
            onChange(newState.data);
          }}
        />
      )}
    </>
  );
};

const TABS = {
  info: 0,
  schedule: 1,
};

const TabPanel = ({ value, index, children }) => (
  <div
    role='tabpanel'
    hidden={value !== index}
    id={`wrapped-tabpanel-${index}`}
    aria-labelledby={`wrapped-tab-${index}`}
  >
    {value === index && <Box p={3}>{children}</Box>}
  </div>
);

const BaseInfo = ({ data, errors, change, submit, user, api, edit }) => {
  const [isFilledInfo, setIsFilledInfo] = React.useState({
    name: Boolean(data.name),
    trainerId: Boolean(data.section),
    section: Boolean(data.trainerId),
    gymId: Boolean(data.gymId),
    canSubmit:
      Boolean(data.name) &&
      Boolean(data.section) &&
      Boolean(data.trainerId) &&
      Boolean(data.gymId),
  });
  const onRequiredChange = (v) => {
    change(v);
    let newIsFilledState = { ...isFilledInfo };
    newIsFilledState[v.target.name] = Boolean(v.target.value);
    newIsFilledState.canSubmit =
      Boolean(newIsFilledState.name) &&
      Boolean(newIsFilledState.section) &&
      Boolean(newIsFilledState.trainerId) &&
      Boolean(newIsFilledState.gymId);
    setIsFilledInfo(newIsFilledState);
  };
  return (
    <>
      <Grid container>
        <Grid item sm={12}>
          <FormBox title={t`Основна інформація`}>
            <FormField
              required
              fullWidth
              type='text'
              margin='normal'
              label={t`Назва группи`}
              id='name'
              name='name'
              autoComplete='name'
              variant='outlined'
              value={data.name}
              errors={errors.name}
              onChange={onRequiredChange}
            />
            {(isIntersection(user.types, [
              USER_TYPES.superManager,
              USER_TYPES.manager,
            ]) ||
              user.isAdmin) && (
              <FormField
                required
                fullWidth
                query={api.schema.trainers.many()[0]}
                preInit={edit}
                dataKey='trainers'
                variables={{ inputFilter: { status: USER_STATUS.active } }}
                type='remoteSelect'
                margin='normal'
                label={t`Виберіть тренера`}
                id='trainerId'
                name='trainerId'
                variant='outlined'
                value={data.trainerId}
                errors={errors.trainerId}
                labelKey='fullName'
                onChange={onRequiredChange}
              />
            )}
            <FormField
              required
              fullWidth
              query={api.schema.common.sections()[0]}
              preInit={edit}
              dataKey='sections'
              variables={{}}
              type='remoteSelect'
              margin='normal'
              label={t`Оберіть вид спорту`}
              id='section'
              name='section'
              variant='outlined'
              value={data.section}
              errors={errors.section}
              labelKey='name'
              onChange={onRequiredChange}
            />
            <FormField
              required
              fullWidth
              query={api.schema.gyms.many()[0]}
              preInit
              dataKey='gyms'
              variables={{}}
              type='remoteSelect'
              margin='normal'
              label={t`Зал (по замовченню)`}
              name='gymId'
              variant='outlined'
              value={data.gymId}
              labelKey='name'
              onChange={onRequiredChange}
            />
          </FormBox>
        </Grid>
        <Grid item sm={12}>
          <FormBox title={t`Спортсмени`}>
            <AthletesForm
              groupId={data.id}
              trainerId={data.trainerId}
              data={data.athleteIds}
              submit={submit}
              canSubmit={isFilledInfo.canSubmit}
              onChange={(d) =>
                change({ target: { name: 'athleteIds', value: d } })
              }
              section={data.section}
            />
          </FormBox>
        </Grid>
      </Grid>
    </>
  );
};

const Schedule = ({ data, change, errors }) => (
  <Grid container spacing={4}>
    <Grid item sm={8}>
      <FormBox title={t`Розклад`}>
        <ScheduleForm
          defaultGymId={data.gymId}
          data={data.schedule}
          onChange={(d) => change({ target: { name: 'schedule', value: d } })}
        />
        {errors.schedule && (
          <ErrorMessage style={{ color: 'red' }} errors={errors.schedule} />
        )}
      </FormBox>
    </Grid>
  </Grid>
);

const BaseForm = ({ initial, edit, onSubmit, loading }) => {
  const api = useApi();
  const { user } = useUser();
  const [tab, setSab] = React.useState(TABS.info);
  const validationSchema = Yup.object({
    name: Yup.string().required(),
    trainerId: Yup.number().nullable().required(),
    section: Yup.number().nullable().required(),
  });
  return (
    <NewForm
      onSubmit={onSubmit}
      onError={() => {
        document.body.scrollTop = document.documentElement.scrollTop = 0;
        setSab(TABS.info);
      }}
      initial={initial}
      validationSchema={validationSchema}
    >
      {({ data, change, errors, submit }) => (
        <>
          <Tabs
            value={tab}
            indicatorColor='primary'
            textColor='primary'
            onChange={(e, v) => setSab(v)}
          >
            <Tab label={t`Основна Інформація`} />
            <Tab label={t`Розклад`} />
          </Tabs>
          <TabPanel value={tab} index={TABS.info}>
            <BaseInfo
              data={data}
              errors={errors}
              change={change}
              submit={submit}
              user={user}
              api={api}
              edit={edit}
            />
          </TabPanel>
          <TabPanel value={tab} index={TABS.schedule}>
            <Schedule data={data} change={change} errors={errors} />
          </TabPanel>
          <LoadingButton
            loading={loading}
            style={{ margin: '20px 0 20px 0' }}
            margin='normal'
            type='button'
            fullWidth
            variant='contained'
            color='primary'
            onClick={submit}
          >
            {t`Зберегти`}
          </LoadingButton>
        </>
      )}
    </NewForm>
  );
};

const getInitialData = (row) => ({
  id: row.id || null,
  name: row.name || '',
  trainerId: row.trainerId || null,
  section: row.section || null,
  schedule: row.schedule || [],
  athleteIds: row.athletes || [],
  gymId: row.gymId || null,
});

const getPostData = (row) => ({
  name: row.name || '',
  trainerId: row.trainerId || null,
  section: row.section || null,
  schedule: row.schedule.map((r) => initScheduleDayData(r)),
  athleteIds: row.athleteIds.map((r) => r.id),
  gymId: row.gymId || null,
});

export const EditForm = () => {
  const api = useApi();
  const { loading, data, onSubmit, submitLoading, errors } = useEditForm({
    schema: api.schema.groups,
    router: api.router.groups,
  });

  if (loading) return <Loading />;

  return (
    <>
      <h2>{t`Редагувати групу: ${data.name}`}</h2>
      <Paper>
        <Box m={4}>
          <BaseForm
            loading={submitLoading}
            initial={getInitialData(data)}
            onSubmit={(formData) => onSubmit(getPostData(formData))}
            errors={errors}
            edit
          />
        </Box>
      </Paper>
    </>
  );
};

export const CreateForm = () => {
  const { user } = useUser();
  let trainerId;
  if (
    isIntersection(user.types, [USER_TYPES.superManager, USER_TYPES.manager])
  ) {
    trainerId = undefined;
  } else if (isIntersection(user.types, [USER_TYPES.trainer])) {
    trainerId = user.id;
  }
  const api = useApi();
  const { loading, onSubmit, submitLoading, errors } = useCreateForm({
    schema: api.schema.groups,
    router: api.router.groups,
  });

  const athletesRes = useQuery(api.schema.athletes.many()[0], {
    fetchPolicy: 'no-cache',
  });

  if (loading) return <Loading />;

  return (
    <>
      <h2>{t`Додати групу`}</h2>
      <Paper>
        <Box m={4}>
          <BaseForm
            loading={submitLoading}
            initial={getInitialData({
              trainerId,
              athletes: athletesRes.data && athletesRes.data.athletes,
            })}
            onSubmit={(formData) => onSubmit(getPostData(formData))}
            errors={errors}
            edit={false}
          />
        </Box>
      </Paper>
    </>
  );
};
